import { inject, Injectable } from "@angular/core";

import { HtmlDirectiveEvaluatorSettings, HtmlSequence } from "@main/reporting.v3/widget.html/models";
import {
    HtmlBaseExpressionsResultService
} from "@main/reporting.v3/widget.html/services/html-expressions/html-base-expressions-result.service";
import {
    HtmlBaseExpressionsEvaluatorService
} from "@main/reporting.v3/widget.html/services/html-expressions/html-base-expressions-evaluator.service";


@Injectable()
export class HtmlBaseExpressionsArgumentService {

    private evaluatorService: HtmlBaseExpressionsEvaluatorService = inject(HtmlBaseExpressionsEvaluatorService);
    private resultService: HtmlBaseExpressionsResultService = inject(HtmlBaseExpressionsResultService);

    public async evaluateAll(sequences: HtmlSequence[], settings: HtmlDirectiveEvaluatorSettings): Promise<any[]> {
        if (!sequences?.length) return [];

        // Resolve all sequences in parallel.
        const promises: Promise<any>[] = sequences.map(sequence => this.evaluate(sequence, settings));

        return await Promise.all(promises);
    }

    public async evaluate(sequence: HtmlSequence, settings: HtmlDirectiveEvaluatorSettings): Promise<any> {
        if (!sequence?.children?.length) return null;

        // If the sequence holds a single child, return its evaluation.
        if (sequence.children.length === 1) {
            return this.evaluatorService.evaluate(sequence.children[0], settings);
        }

        // Otherwise, evaluate all its children and collapse the results as string.
        const results: any[] = await this.evaluatorService.evaluate(sequence, settings);

        return this.resultService.collapse(results);
    }

    public unescape(string: string): string {
        return string
            .replace(/&lt;/g, "<")
            .replace(/&gt;/g, ">")
            .replace(/&#39;/g, "'")
            .replace(/&amp;/g, "&")
            .replace(/&quot;/g, "\"");
    }

    public toNumber(value: any): number {
        if (value == null) return value;

        // If the value is not a basic type, it is not convertible.
        if (!this.resultService.supported(value)) return null;

        // If the value is an empty string, it is not convertible.
        if (typeof value === "string" && !value.trim()) return null;

        const number: number = Number(value);

        // If the value as number is NaN, it is not convertible.
        return !isNaN(number) ? number : null;
    }

    public toBoolean(value: any): boolean {
        if (value == null) return value;

        if (value === "true") return true;

        if (value === "false") return false;

        return Boolean(value);
    }

    public toString(value: any): string {
        if (value == null) return value;

        // Consider 'null' string as null value.
        if (/\s*null\s*/.test(value)) return null;

        // Convert the value into a supported HTML type.
        const conversion: string | Node = this.resultService.convert(value);

        // If the result is a node, return null.
        if (conversion instanceof Node) return null;

        // Unescape the html escaped characters present in the text.
        return this.unescape(conversion).trim();
    }

    public toListString(expression: string): string[] {
        if (!expression?.length) return null;

        try {
            const result: any = JSON.parse(expression);

            // If the result is a list, stringify all its items and return it.
            if (result instanceof Array) {
                return result.map(value => this.resultService.stringify(value));
            }

            // Otherwise, return a list containing the original expression.
            return [expression];

        } catch {
            // If parsing fails, return a list containing the original expression.
            return [expression];
        }
    }
}